import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import { AnswerID } from '@aytm/app/swidget/base/questions/types'
import { formatPriceWithLocale } from '../../functions'
import { localizeCurrency, qtyCap } from '../functions'
import { EcommerceResponse, IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types'
import Card from './Card'
import commonStyle from '../Chewy.module.scss'
import style from './Cart.module.scss'
import trashIcon from '../assets/icons/trash.svg'
import shipsFreeIcon from '../assets/icons/ships-free.svg'
import paypalIcon from '../assets/icons/paypal.svg'
import emptyCartIllustration from '../assets/icons/empty-cart.svg'

const FREE_SHIPPING_THRESHOLD = 49

interface ICartProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  totals: number[]
  findability: boolean
  onUpdateResponse: (response: EcommerceResponse) => void
  onItemSelect: (id: AnswerID) => void
  onAddToCart: (id: AnswerID, qnt?: number) => void
  onSetQuantity: (id: AnswerID, qnt: number) => void
  onCartClose: () => void
  checkout: () => void
  scrollTop: () => void
}

const Cart = ({
  schema,
  response,
  totals,
  findability,
  onUpdateResponse,
  onItemSelect,
  onAddToCart,
  onSetQuantity,
  onCartClose,
  checkout,
  scrollTop
}: ICartProps) => {
  const { t, languageTag } = useSurveyLocale()
  const timeline = useTimeline()
  const viewport = useViewport()
  const na = useMemo(() => schema.answers.find(answer => answer.na), [schema])

  const valid =
    (schema.max_money_spent ? totals[1] <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? totals[0] <= schema.max_cart_size : true)

  // Budget message carries a price mask (`formatPriceWithLocale` returns markup), so the banner is
  // rendered as HTML (mirrors the other retailers); present messages join with a space so the two
  // sentences don't run together.
  const cartError = [
    schema.max_money_spent && totals[1] > schema.max_money_spent
      ? t<(amount: string) => string>(
          'questions',
          'ec',
          'chewy',
          'maxMoneyError'
        )(formatPriceWithLocale(schema.max_money_spent, schema.price_mask, languageTag, true, false))
      : '',
    schema.max_cart_size && totals[0] > schema.max_cart_size
      ? t<(max: number) => string>('questions', 'ec', 'chewy', 'maxItemsError')(schema.max_cart_size)
      : ''
  ]
    .filter(Boolean)
    .join(' ')

  const schemaById = useMemo(() => new Map(schema.answers.map(answer => [answer.id, answer])), [schema.answers])

  const itemsInCart = useMemo(
    () => response.answers.filter(answer => answer.v > 0 && answer.id !== na?.id),
    [response, na]
  )

  // "Inspired by your cart" = author-curated featured products the shopper hasn't added yet.
  const inspired = useMemo(() => {
    const inCartIds = new Set(response.answers.filter(answer => answer.v > 0).map(answer => answer.id))
    return schema.answers.filter(
      answer => answer.featured && !answer.parent_product && !answer.na && !answer.banner && !inCartIds.has(answer.id)
    )
    // Depend on `response` (a fresh object each update), not `response.answers` — the answers array
    // is mutated in place and keeps its reference, so it would never re-trigger this memo.
  }, [schema.answers, response])

  const cap = qtyCap(schema.max_qty_per_product, findability)

  useEffect(() => {
    scrollTop()
  }, [scrollTop])

  // "Inspired by your cart" nav arrows — mirrors SponsoredCarousel: arrows show only when the track
  // overflows, and each disables at its edge. Re-measure on resize, image load, and scroll.
  const inspiredTrackRef = useRef<HTMLDivElement>(null)
  const [inspiredNav, setInspiredNav] = useState({ overflowing: false, atStart: true, atEnd: false })

  const measureInspiredNav = useCallback(() => {
    const track = inspiredTrackRef.current
    if (!track) return
    const { scrollLeft, scrollWidth, clientWidth } = track
    const next = {
      overflowing: scrollWidth > clientWidth + 1,
      atStart: scrollLeft <= 0,
      atEnd: scrollLeft + clientWidth >= scrollWidth - 1
    }
    setInspiredNav(prev =>
      prev.overflowing === next.overflowing && prev.atStart === next.atStart && prev.atEnd === next.atEnd ? prev : next
    )
  }, [])

  useLayoutEffect(() => {
    const track = inspiredTrackRef.current
    if (!track) return
    measureInspiredNav()
    const ro = new ResizeObserver(measureInspiredNav)
    ro.observe(track)
    return () => ro.disconnect()
  }, [inspired.length, measureInspiredNav])

  useEffect(() => {
    const track = inspiredTrackRef.current
    if (!track) return
    const imgs = Array.from(track.querySelectorAll('img'))
    if (imgs.length === 0) return
    imgs.forEach(img => img.addEventListener('load', measureInspiredNav))
    return () => imgs.forEach(img => img.removeEventListener('load', measureInspiredNav))
  }, [inspired.length, measureInspiredNav])

  const slideInspired = (direction: 1 | -1) => {
    if (!inspiredTrackRef.current) return
    const slideWidth = inspiredTrackRef.current.clientWidth / 2
    inspiredTrackRef.current.scrollBy({ left: direction * slideWidth, behavior: 'smooth' })
  }

  // Cart dropdown sets an absolute quantity; remove sets it to 0 (ChewyView logs delete_from_cart).
  const onQuantityChange = (id: AnswerID, next: number) => onSetQuantity(id, next)
  const onRemove = (id: AnswerID) => onSetQuantity(id, 0)

  const onNaSelect = () => {
    if (!na) return
    timeline.log({
      type: 'na_select',
      ref: na.id
    })
    response.answers = response.answers.map(answer => ({
      ...answer,
      v: answer.id === na.id ? 1 : 0
    }))
    onUpdateResponse(response)
  }

  const subtotalOriginal = response.answers.reduce((sum, r) => sum + r.v * (schemaById.get(r.id)?.price ?? 0), 0)
  const hasDiscount = subtotalOriginal > totals[1]
  const qualifiesForFreeShipping = totals[1] >= FREE_SHIPPING_THRESHOLD
  const monthlyPayment = totals[1] > 0 ? Math.round((totals[1] / 4) * 100) / 100 : 0

  // Cart line shows the per-unit price (matches real Chewy + the Amazon sim); the line total
  // lives only in the order summary. Quantity is already shown in the line's qty dropdown.
  const renderItemPrice = (answer: IEcommerceAnswerSchema) => {
    const itemHasDiscount = Boolean(answer.discounted_price) && answer.price > answer.discounted_price
    const unitPrice = itemHasDiscount ? answer.discounted_price : answer.price
    return (
      <span
        className={`${style.itemPriceCurrent} ${itemHasDiscount ? style.itemPriceCurrentDiscounted : ''}`}
        dangerouslySetInnerHTML={{
          __html: formatPriceWithLocale(unitPrice, schema.price_mask, languageTag, true, false)
        }}
      />
    )
  }

  if (itemsInCart.length === 0) {
    return (
      <section className={`${style.cart} ${viewport.mobile ? style.mobile : ''}`} data-testid="SW-EC-Cart">
        {viewport.mobile && (
          <header className={style.mobileHeader}>
            <h1 className={style.mobileHeaderTitle}>{t('questions', 'ec', 'chewy', 'shoppingCart')}</h1>
            <button
              type="button"
              className={style.mobileHeaderClose}
              onClick={onCartClose}
              aria-label={t('questions', 'ec', 'chewy', 'close')}
            >
              <i className="pi pi-times" />
            </button>
          </header>
        )}
        <div className={style.empty}>
          <h1 className={style.emptyTitle}>{t('questions', 'ec', 'chewy', 'emptyTitle')}</h1>
          <p className={style.emptyText}>
            {t('questions', 'ec', 'chewy', 'emptySomethingMissing')}{' '}
            <EmptyLink color="#1C49C4">
              <span className={style.emptySignInLink}>{t('questions', 'ec', 'chewy', 'signInLink')}</span>
            </EmptyLink>{' '}
            {t('questions', 'ec', 'chewy', 'emptySeeItems')}
          </p>
          <div className={style.emptyArt}>
            <img src={emptyCartIllustration} alt="" role="presentation" />
          </div>
          {/* Sign-in is inert in the simulator — EmptyLink shows the "simulation" tooltip on click */}
          <EmptyLink color="#1C49C4">
            <span className={style.emptyPrimaryBtn}>{t('questions', 'ec', 'chewy', 'signInCreateAccount')}</span>
          </EmptyLink>
          <button type="button" className={style.emptySecondaryBtn} onClick={onCartClose}>
            {t('questions', 'ec', 'chewy', 'shopTodaysDeals')}
          </button>
          {na && (
            <button type="button" className={style.naBtn} onClick={onNaSelect}>
              {na.text || t('questions', 'ec', 'chewy', 'noneOfThese')}
            </button>
          )}
        </div>
      </section>
    )
  }

  return (
    <section className={`${style.cart} ${viewport.mobile ? style.mobile : ''}`} data-testid="SW-EC-Cart">
      {viewport.mobile ? (
        <header className={style.mobileHeader}>
          <h1 className={style.mobileHeaderTitle}>{t('questions', 'ec', 'chewy', 'shoppingCart')}</h1>
          <button
            type="button"
            className={style.mobileHeaderClose}
            onClick={onCartClose}
            aria-label={t('questions', 'ec', 'chewy', 'close')}
          >
            <i className="pi pi-times" />
          </button>
        </header>
      ) : (
        <button type="button" className={style.back} onClick={onCartClose}>
          <i className="pi pi-angle-left" />
          <span>{t('questions', 'ec', 'chewy', 'continueShopping')}</span>
        </button>
      )}

      <div className={style.body}>
        <div className={style.itemsCol}>
          <div className={style.itemsCard}>
            <header className={style.itemsHeader}>
              <h1 className={style.itemsTitle}>{t('questions', 'ec', 'chewy', 'shoppingCart')}</h1>
            </header>

            {itemsInCart.map(respAnswer => {
              const schemaAnswer = schemaById.get(respAnswer.id)
              if (!schemaAnswer) return null
              return (
                <div className={style.item} key={schemaAnswer.id} data-testid="SW-EC-CartItem">
                  <div className={style.itemImageWrap} onClick={() => onItemSelect(schemaAnswer.id)}>
                    {/* Cart thumbnail shows a single badge: red_label takes precedence, overal_pick ("New") is the fallback. */}
                    {(schemaAnswer.red_label || schemaAnswer.overal_pick) && (
                      <div className={commonStyle.badgeRow}>
                        {/* HTML by design (author markup / search highlight); XSS-safe — sanitized server-side on ingress: author via SurveySchemaTexts.safe_string?, import via Trajectdata::DataSanitizer. */}
                        {schemaAnswer.red_label ? (
                          <div
                            className={commonStyle.redLabel}
                            dangerouslySetInnerHTML={{ __html: schemaAnswer.red_label }}
                          />
                        ) : (
                          <div className={commonStyle.newBadge} />
                        )}
                      </div>
                    )}
                    {schemaAnswer.image ? (
                      <div
                        className={style.itemImage}
                        style={{ backgroundImage: `url('${getMediumAssetPath(schemaAnswer.image)}')` }}
                      />
                    ) : (
                      <div className={style.itemNoImage} />
                    )}
                  </div>

                  <div className={style.itemBody}>
                    <div
                      className={style.itemTitle}
                      onClick={() => onItemSelect(schemaAnswer.id)}
                      dangerouslySetInnerHTML={{ __html: schemaAnswer.text }}
                    />
                    <div className={style.itemPriceRow}>{renderItemPrice(schemaAnswer)}</div>
                    <div className={style.itemMeta}>
                      {localizeCurrency(t('questions', 'ec', 'chewy', 'cartItemMeta'), schema.price_mask)}{' '}
                      <EmptyLink color="#1C49C4">
                        <span className={style.itemMetaCode}>{t('questions', 'ec', 'chewy', 'welcomeCode')}</span>
                      </EmptyLink>{' '}
                      &middot;{' '}
                      <EmptyLink color="#1C49C4">
                        <span className={style.itemMetaLink}>{t('questions', 'ec', 'chewy', 'details')}</span>
                      </EmptyLink>
                    </div>
                  </div>

                  <div className={style.itemControls}>
                    <div className={style.qtyWrap}>
                      <select
                        className={style.qtySelect}
                        aria-label={t('questions', 'ec', 'chewy', 'quantity')}
                        value={respAnswer.v}
                        onChange={event => onQuantityChange(schemaAnswer.id, Number(event.target.value))}
                      >
                        {Array.from({ length: Math.max(cap, respAnswer.v) }, (_, i) => i + 1).map(n => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button type="button" className={style.removeBtn} onClick={() => onRemove(schemaAnswer.id)}>
                      <img src={trashIcon} alt="" role="presentation" className={style.removeIcon} />
                      <span>{t('questions', 'ec', 'chewy', 'remove')}</span>
                    </button>
                  </div>
                </div>
              )
            })}

            <div className={style.cashRewardsRow}>
              <span className={style.cashRewardsRadio}>
                <span className={style.cashRewardsRadioDot} />
              </span>
              <div className={style.cashRewardsBody}>
                <span className={style.cashRewardsTitle}>{t('questions', 'ec', 'chewy', 'cashRewards')}</span>
                <span
                  className={style.cashRewardsText}
                  dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'chewy', 'cashRewardsText') }}
                />
              </div>
            </div>
          </div>
        </div>

        <aside className={style.sidebar}>
          <div className={`${style.sidebarCard} ${style.summaryCard}`}>
            <div className={style.subtotalRow}>
              <span className={style.subtotalLabel}>{t('questions', 'ec', 'chewy', 'subtotal')}</span>
              <span className={style.subtotalValueGroup}>
                <span
                  className={style.subtotalValue}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false)
                  }}
                />
                {hasDiscount && (
                  <span
                    className={style.subtotalOld}
                    dangerouslySetInnerHTML={{
                      __html: formatPriceWithLocale(subtotalOriginal, schema.price_mask, languageTag, true, false)
                    }}
                  />
                )}
              </span>
            </div>

            <div className={style.subtotalRow}>
              <span className={style.subtotalCount}>
                {t<(count: number) => string>('questions', 'ec', 'chewy', 'itemsCount')(totals[0])}
              </span>
              <EmptyLink color="#1C49C4">
                <div className={style.estimatedLink}>{t('questions', 'ec', 'chewy', 'seeEstimatedTotal')}</div>
              </EmptyLink>
            </div>

            <div className={style.shipsRow}>
              {qualifiesForFreeShipping ? (
                <span dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'chewy', 'orderShipsFree') }} />
              ) : (
                <span
                  dangerouslySetInnerHTML={{
                    __html: t<(amount: string) => string>(
                      'questions',
                      'ec',
                      'chewy',
                      'addForFreeShipping'
                    )(
                      formatPriceWithLocale(
                        FREE_SHIPPING_THRESHOLD - totals[1],
                        schema.price_mask,
                        languageTag,
                        true,
                        false
                      )
                    )
                  }}
                />
              )}
              <img src={shipsFreeIcon} alt="" role="presentation" className={style.shipsIcon} />
            </div>
            <div className={style.progressBar}>
              <div
                className={style.progressFill}
                style={{
                  width: qualifiesForFreeShipping
                    ? '100%'
                    : `${Math.min(100, (totals[1] / FREE_SHIPPING_THRESHOLD) * 100)}%`
                }}
              />
            </div>

            {!valid && <div className={style.cartError} dangerouslySetInnerHTML={{ __html: cartError }} />}

            <button
              type="button"
              className={`${style.checkoutBtn} ${!valid ? style.checkoutBtnDisabled : ''}`}
              onClick={checkout}
              disabled={!valid}
            >
              {t('questions', 'ec', 'chewy', 'checkout')}
            </button>
          </div>

          <div className={style.sidebarCard}>
            <div className={style.savingsHeader}>
              <span className={style.savingsTitle}>{t('questions', 'ec', 'chewy', 'availableSavings')}</span>
              <EmptyLink color="#1C49C4">
                <span className={style.savingsHelp}>+</span>
              </EmptyLink>
            </div>
          </div>

          <div className={style.sidebarCard}>
            <div className={style.returnsRow}>
              <i className={`pi pi-replay ${style.returnsIcon}`} />
              <div className={style.returnsBody}>
                <span className={style.returnsTitle}>{t('questions', 'ec', 'chewy', 'freeReturns365Caps')}</span>
                <span className={style.returnsSub}>
                  {t('questions', 'ec', 'chewy', 'someExclusions')}{' '}
                  <EmptyLink color="#1C49C4">
                    <span className={style.returnsLink}>{t('questions', 'ec', 'chewy', 'details')}</span>
                  </EmptyLink>
                </span>
              </div>
            </div>
          </div>

          <div className={style.sidebarCard}>
            <div className={style.bnplRow}>
              <img src={paypalIcon} alt="PayPal" className={style.bnplLogo} />
              <div className={style.bnplBody}>
                <span className={style.bnplTitle}>{t('questions', 'ec', 'chewy', 'buyNowPayLater')}</span>
                <span className={style.bnplText}>
                  {t('questions', 'ec', 'chewy', 'interestFreePrefix')}{' '}
                  <strong
                    dangerouslySetInnerHTML={{
                      __html: formatPriceWithLocale(monthlyPayment, schema.price_mask, languageTag, true, false)
                    }}
                  />
                  .{' '}
                  <EmptyLink color="#1C49C4">
                    <span className={style.bnplLink}>{t('questions', 'ec', 'chewy', 'learnMore')}</span>
                  </EmptyLink>
                </span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {inspired.length > 0 && (
        <div className={style.inspiredCard} data-testid="SW-EC-InspiredByYourCart">
          <h2 className={style.inspiredTitle}>{t('questions', 'ec', 'chewy', 'inspiredByCart')}</h2>
          <div className={style.inspiredViewport}>
            {inspiredNav.overflowing && (
              <button
                type="button"
                className={`${style.inspiredNavBtn} ${style.inspiredNavBtnLeft}`}
                disabled={inspiredNav.atStart}
                onClick={() => slideInspired(-1)}
                aria-label={t('questions', 'ec', 'chewy', 'previous')}
              >
                <i className="pi pi-chevron-left" />
              </button>
            )}
            <div ref={inspiredTrackRef} className={style.inspiredTrack} onScroll={measureInspiredNav}>
              {inspired.map(answer => (
                <div key={answer.id} className={style.inspiredSlide}>
                  <Card
                    data-testid="SW-EC-CarouselItem"
                    schema={schema}
                    answer={answer}
                    findability={findability}
                    disabled={'focus' == schema.mode_type && !answer.focused}
                    bordered={true}
                    enableImageGallery={false}
                    onItemSelect={onItemSelect}
                    onAddToCart={onAddToCart}
                    onSetQuantity={onSetQuantity}
                  />
                </div>
              ))}
            </div>
            {inspiredNav.overflowing && (
              <button
                type="button"
                className={`${style.inspiredNavBtn} ${style.inspiredNavBtnRight}`}
                disabled={inspiredNav.atEnd}
                onClick={() => slideInspired(1)}
                aria-label={t('questions', 'ec', 'chewy', 'next')}
              >
                <i className="pi pi-chevron-right" />
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

export default Cart
